import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Phone, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import Seo from "@/lib/seo";
import { track } from "@/lib/tracking";

const TOPICS = [
  "Buyer / seller consultation",
  "See a specific home",
  "Sell my home",
  "Get pre-approved",
  "General questions",
];

function nextDates(n = 14) {
  const out = [];
  const d = new Date();
  while (out.length < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) out.push(new Date(d)); // skip Sundays
  }
  return out;
}

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookCall() {
  const dates = nextDates();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(isoDate(dates[0]));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slot, setSlot] = useState(null);
  // Deep links from listings prefill the topic ("Tour: 123 Main St…")
  const deepTopic = searchParams.get("topic");
  const propertyId = searchParams.get("property_id");
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    topic: deepTopic || TOPICS[0], notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    let on = true;
    setLoadingSlots(true);
    setSlot(null);
    api.get("/bookings/availability", { params: { date } })
      .then(({ data }) => { if (on) setSlots(data.slots || []); })
      .catch(() => { if (on) setSlots([]); })
      .finally(() => { if (on) setLoadingSlots(false); });
    return () => { on = false; };
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) { toast.error("Pick a time first."); return; }
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    if (!form.phone) { toast.error("Phone is required — Lamar calls you at this number."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        name: form.name, email: form.email, phone: form.phone,
        start: slot.start, topic: form.topic, notes: form.notes || null,
        property_id: propertyId || null,
      });
      track("booking", { topic: form.topic });
      setDone({ ...data, label: slot.label, dateLabel: new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // a 409 likely means the slot was taken — refresh availability
      api.get("/bookings/availability", { params: { date } }).then(({ data }) => setSlots(data.slots || []));
      setSlot(null);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = "w-full bg-[#0B0C10] border border-white/10 rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50";
  const labelCls = "block text-[11px] uppercase tracking-wider text-white/40 mb-1.5";

  return (
    <div className="bg-background min-h-screen">
      <Seo
        title="Book a Free Call"
        description="Book a free 30-minute call with Lamar A. Whyte, REALTOR® — buying, selling, or renting in New York, no pressure."
        path="/book"
      />
      <SiteNav />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-8 text-center">
          <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">No phone tag</p>
          <h1 className="text-4xl md:text-5xl text-white flex items-center justify-center gap-3">
            <CalendarCheck size={36} weight="duotone" className="text-brand" /> Book a call with Lamar
          </h1>
          <p className="text-white/50 mt-3">Pick a time that works for you — you'll get a calendar invite instantly.</p>
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-[#101116] border border-brand/20 rounded-sm p-8 text-center" data-testid="book-success">
            <CheckCircle size={48} weight="fill" className="mx-auto text-brand mb-4" />
            <h2 className="text-2xl font-serif text-white">You're booked!</h2>
            <p className="text-white/70 mt-2">{done.dateLabel} at {done.label} ET</p>
            <p className="text-white/50 text-sm mt-4 max-w-md mx-auto">
              A confirmation with a calendar invite is on its way to <span className="text-white">{done.email}</span>. Lamar will call you then.
            </p>
            <a href="/" className="inline-block mt-6 text-brand text-sm hover:text-brand-hover">Back to home</a>
          </motion.div>
        ) : (
          <div className="bg-[#101116] border border-white/5 rounded-sm p-6 sm:p-8" data-testid="book-page">
            {/* Date picker */}
            <p className={labelCls}>Choose a day</p>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
              {dates.map((d) => {
                const iso = isoDate(d);
                const active = iso === date;
                return (
                  <button
                    key={iso}
                    onClick={() => setDate(iso)}
                    data-testid={`book-date-${iso}`}
                    className={`shrink-0 rounded-sm border px-3.5 py-2 text-center transition-colors ${active ? "border-brand bg-brand/10 text-brand" : "border-white/10 text-white/60 hover:text-white"}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className="text-sm font-medium">{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  </button>
                );
              })}
            </div>

            {/* Slots */}
            <p className={labelCls}>Available times (ET)</p>
            {loadingSlots ? (
              <div className="py-8 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
            ) : slots.length === 0 ? (
              <p className="py-6 text-center text-sm text-white/40" data-testid="book-no-slots">No times left this day — try another date.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6" data-testid="book-slots">
                {slots.map((s) => (
                  <button
                    key={s.start}
                    onClick={() => setSlot(s)}
                    data-testid={`book-slot-${s.start}`}
                    className={`rounded-sm border py-2 text-sm transition-colors ${slot?.start === s.start ? "border-brand bg-brand text-black font-medium" : "border-white/10 text-white/70 hover:border-brand/40"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Details form */}
            {slot && (
              <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={submit} className="border-t border-white/5 pt-6 space-y-4">
                <p className="text-sm text-white/70">Selected: <span className="text-brand">{new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {slot.label} ET</span></p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Your name</label>
                    <input data-testid="book-name" className={fieldCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input data-testid="book-email" type="email" className={fieldCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input data-testid="book-phone" type="tel" className={fieldCls} value={form.phone} onChange={set("phone")} placeholder="+1 (555) 123-4567" />
                  </div>
                  <div>
                    <label className={labelCls}>Topic</label>
                    <select data-testid="book-topic" className={fieldCls} value={form.topic} onChange={set("topic")}>
                      {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Anything I should know? (optional)</label>
                  <textarea data-testid="book-notes" rows={2} className={`${fieldCls} resize-none`} value={form.notes} onChange={set("notes")} placeholder="Neighborhoods, budget, timeline…" />
                </div>
                <button type="submit" disabled={submitting} data-testid="book-submit" className="w-full flex items-center justify-center gap-2 rounded-sm bg-brand py-3 text-sm font-medium uppercase tracking-wider text-black hover:bg-brand-hover disabled:opacity-60">
                  {submitting ? "Booking…" : <><Phone size={16} weight="fill" /> Confirm my call</>}
                </button>
              </motion.form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
